// ============================================================
// PanelIdentityHeader - who this panel is about.
//
// The left panel used to be titled the literal string "ATTACKER",
// which named a role in one dice roll rather than the thing on screen:
// it holds a whole character. The name IS the title now, and the
// picker is the name - no button chrome, so choosing who you are
// recedes and who you are dominates.
//
// The right panel gets the opposite treatment. Targets are the mobile,
// ephemeral half of a fight, so it reads as an instrument: a reticle,
// a danger-toned eyebrow, and ◀ ▶ to cycle without opening anything.
//
// One component, differing only by props - the two headers were
// structurally identical once the literals were removed.
// ============================================================
import type { ReactNode } from 'react';
import clsx from '../utils/clsx';
import CombatantVitals from './CombatantVitals';
import { defenceChips, StatusChipView } from './StatusBand';
import type { Target } from '../types';
import styles from '../styles/PanelIdentityHeader.module.css';

export interface PanelIdentityHeaderProps {
  /** Small dim label above the name: "ACTING AS" / "TARGETING". */
  eyebrow: string;
  icon?: string;
  name: string;
  /** Projected defender, for the persistent health strip. Null = nobody. */
  target?: Target | null;
  onNameClick: () => void;
  /**
   * MUST stay "Select acting character" / "Select target" - a pile of
   * specs find these panels by that label.
   */
  nameAriaLabel: string;
  expanded?: boolean;
  disabled?: boolean;
  tone?: 'accent' | 'danger';
  /** Cycle through the roster without opening the picker. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Panel-specific extras: the GM lock glyph, the Templates menu. */
  trailing?: ReactNode;
}

export default function PanelIdentityHeader({
  eyebrow,
  icon,
  name,
  target = null,
  onNameClick,
  nameAriaLabel,
  expanded = false,
  disabled = false,
  tone = 'accent',
  onPrev,
  onNext,
  trailing,
}: PanelIdentityHeaderProps) {
  return (
    <div className={clsx(styles.header, tone === 'danger' && styles.danger)} data-testid="panel-identity">
      <div className={styles.topRow}>
        <span className={styles.eyebrow}>
          {icon && <span aria-hidden="true">{icon} </span>}
          {eyebrow}
        </span>
        <span className={styles.controls}>
          {onPrev && (
            <button type="button" className={styles.cycle} aria-label="Previous target" onClick={onPrev}>
              ◀
            </button>
          )}
          {onNext && (
            <button type="button" className={styles.cycle} aria-label="Next target" onClick={onNext}>
              ▶
            </button>
          )}
          {trailing}
        </span>
      </div>

      <button
        type="button"
        className={styles.name}
        aria-label={nameAriaLabel}
        aria-expanded={expanded}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={onNameClick}
      >
        <span className={styles.nameText}>{name}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>

      {/* Persistent health, so wounds are readable from Armory or
          Controls without going back to Status. */}
      {target && <CombatantVitals target={target} layout="strip" showBar name={name} />}

      {target && <HeaderChipRail target={target} />}
    </div>
  );
}

/**
 * The defence chips too (v8.241.0): force field, cover, stance, frenzy,
 * facing - the things checked before every roll, on screen from every
 * tab. One line; overflow fades out and the chip titles carry the
 * detail. Prefix `header-chip`: status-band E2E addresses status-chip-*
 * UNSCOPED.
 */
function HeaderChipRail({ target }: { target: Target }) {
  const chips = defenceChips(target);
  if (chips.length === 0) return null;
  return (
    <div className={styles.chipRail} data-testid="header-chip-rail">
      {chips.map((c) => (
        <StatusChipView key={c.key} chip={c} testidPrefix="header-chip" />
      ))}
    </div>
  );
}
